import api from "./api"


// =========================================================
// HELPERS
// =========================================================

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
    )
  )


const appendFormValue = (
  formData,
  key,
  value
) => {
  if (
    value !== undefined &&
    value !== null &&
    value !== ""
  ) {
    formData.append(
      key,
      value
    )
  }
}


// =========================================================
// FORM DATA
// =========================================================

export const getHrContractFormData = async () => {
  const response = await api.get(
    "/hr/contracts/form-data"
  )

  return response.data
}


// =========================================================
// SUMMARY
// =========================================================

export const getHrContractSummary = async () => {
  const response = await api.get(
    "/hr/contracts/summary"
  )

  return response.data
}


// =========================================================
// CONTRACTS - LIST
// =========================================================

export const getHrContracts = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/contracts",
    {
      params:
        cleanParams(
          params
        ),
    }
  )

  return response.data
}


// =========================================================
// CONTRACT ALIASES
// =========================================================

export const getContracts =
  getHrContracts

export const listContracts =
  getHrContracts

export const getContractList =
  getHrContracts


// =========================================================
// CONTRACT DETAIL
// =========================================================

export const getHrContractById = async (
  contractId
) => {
  const response = await api.get(
    `/hr/contracts/${contractId}`
  )

  return response.data
}


// =========================================================
// CONTRACT CREATE
// =========================================================

export const createHrContract = async (
  payload
) => {
  const response = await api.post(
    "/hr/contracts",
    payload
  )

  return response.data
}


// =========================================================
// CONTRACT UPDATE
// =========================================================

export const updateHrContract = async (
  contractId,
  payload
) => {
  const response = await api.put(
    `/hr/contracts/${contractId}`,
    payload
  )

  return response.data
}


// =========================================================
// CONTRACT RENEW
// =========================================================

export const renewHrContract = async (
  contractId,
  payload
) => {
  const response = await api.post(
    `/hr/contracts/${contractId}/renew`,
    payload
  )

  return response.data
}


// =========================================================
// CONTRACT TERMINATE
// =========================================================

export const terminateHrContract = async (
  contractId,
  payload
) => {
  const response = await api.post(
    `/hr/contracts/${contractId}/terminate`,
    payload
  )

  return response.data
}


// =========================================================
// REFRESH EXPIRED
// =========================================================

export const refreshExpiredHrContracts = async () => {
  const response = await api.post(
    "/hr/contracts/refresh-expired",
    {}
  )

  return response.data
}


// =========================================================
// DOCUMENTS - LIST
// =========================================================

export const getHrDocuments = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/documents",
    {
      params:
        cleanParams(
          params
        ),
    }
  )

  return response.data
}


// =========================================================
// DOCUMENT DETAIL
// =========================================================

export const getHrDocumentById = async (
  documentId
) => {
  const response = await api.get(
    `/hr/documents/${documentId}`
  )

  return response.data
}


// =========================================================
// DOCUMENT CREATE - JSON COMPATIBILITY
// =========================================================

export const createHrDocument = async (
  payload
) => {
  const response = await api.post(
    "/hr/documents",
    payload
  )

  return response.data
}


// =========================================================
// DOCUMENT UPLOAD - REAL FILE
// =========================================================

export const uploadHrDocument = async (
  payload,
  file,
  onUploadProgress
) => {
  const formData = new FormData()

  appendFormValue(
    formData,
    "organization_id",
    payload?.organization_id
  )

  appendFormValue(
    formData,
    "employee_id",
    payload?.employee_id
  )

  appendFormValue(
    formData,
    "contract_id",
    payload?.contract_id
  )

  appendFormValue(
    formData,
    "document_type",
    payload?.document_type
  )

  appendFormValue(
    formData,
    "title",
    payload?.title
  )

  appendFormValue(
    formData,
    "issue_date",
    payload?.issue_date
  )

  appendFormValue(
    formData,
    "expiry_date",
    payload?.expiry_date
  )

  appendFormValue(
    formData,
    "notes",
    payload?.notes
  )

  if (file) {
    formData.append(
      "file",
      file
    )
  }

  const response = await api.post(
    "/hr/documents/upload",
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data",
      },
      onUploadProgress,
    }
  )

  return response.data
}


// =========================================================
// DOCUMENT UPDATE
// =========================================================

export const updateHrDocument = async (
  documentId,
  payload
) => {
  const response = await api.put(
    `/hr/documents/${documentId}`,
    payload
  )

  return response.data
}


// =========================================================
// DOCUMENT FILE - OPEN / DOWNLOAD
// =========================================================

export const getHrDocumentFile = async (
  documentId,
  options = {}
) => {
  const response = await api.get(
    `/hr/documents/${documentId}/file`,
    {
      params: {
        download:
          options.download
          ? true
          : undefined,
      },
      responseType: "blob",
    }
  )

  return response
}


export const openHrDocumentFile = async (
  documentId
) =>
  getHrDocumentFile(
    documentId,
    {
      download: false,
    }
  )


export const downloadHrDocumentFile = async (
  documentId
) =>
  getHrDocumentFile(
    documentId,
    {
      download: true,
    }
  )


// =========================================================
// DOCUMENT DELETE
// =========================================================

export const deleteHrDocument = async (
  documentId
) => {
  const response = await api.delete(
    `/hr/documents/${documentId}`
  )

  return response.data
}


// =========================================================
// GENERIC COMPATIBILITY ALIASES
// =========================================================

export const createContract =
  createHrContract

export const updateContract =
  updateHrContract

export const renewContract =
  renewHrContract

export const terminateContract =
  terminateHrContract

export const getDocuments =
  getHrDocuments

export const createDocument =
  createHrDocument

export const uploadDocument =
  uploadHrDocument

export const updateDocument =
  updateHrDocument

export const deleteDocument =
  deleteHrDocument
